/**
 * ChatArtifact — inline rich cards the FORGE Operator Agent renders in the chat when
 * it calls a read tool (S1: line items / suppliers / quotes / validation). Read-only
 * for now; action buttons (Award, Accept…) arrive in S2.
 */
import React, { useState } from 'react';
import { Package, Factory, Tag, ShieldCheck, AlertTriangle, Gavel, Check, X, Zap, TrendingUp, ExternalLink, MessageSquare } from 'lucide-react';

const money = (v, cur = 'USD') => (v == null ? '—' : `${cur === 'USD' ? '$' : ''}${v}${cur !== 'USD' ? ` ${cur}` : ''}`);

const SUPPLIER_STATUS = {
  accepted: ['Accepted', '#059669'], candidate: ['Candidate', '#6b7785'],
  rejected: ['Rejected', '#b91c1c'],
};

// Live Accio/Alibaba negotiation state (from log_negotiation thread.state).
const NEG_STATE = {
  new: ['Not contacted', '#6b7785'], awaiting_supplier: ['Awaiting supplier', '#d97706'],
  awaiting_us: ['Your turn', '#2563eb'], quoted: ['Quoted', '#059669'],
  needs_human: ['Needs review', '#b91c1c'], closed: ['Closed', '#6b7785'],
};

// Minutes-ago helper for "last heard from Accio".
const ago = (ts) => {
  if (!ts) return '';
  const s = (Date.now() - new Date(ts).getTime()) / 1000;
  if (Number.isNaN(s)) return '';
  if (s < 90) return 'just now';
  if (s < 3600) return `${Math.round(s / 60)}m ago`;
  if (s < 86400) return `${Math.round(s / 3600)}h ago`;
  return `${Math.round(s / 86400)}d ago`;
};

const ConvLink = ({ url }) => (url ? (
  <a className="forge-art-conv" href={url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}>
    <ExternalLink size={11} /> View negotiation
  </a>
) : null);

const LineItems = ({ items = [] }) => (
  <div className="forge-art">
    <div className="forge-art-hd"><Package size={13} /> Line items · {items.length}</div>
    {items.map((li) => (
      <div key={li.n} className="forge-art-row">
        <span className="forge-art-num">#{li.n}</span>
        <span className="forge-art-main">{li.product}</span>
        <span className="forge-art-sub">
          {li.quantity != null ? `${li.quantity} ${li.unit || 'ea'}` : ''}
          {li.certs?.length ? ` · ${li.certs.join(', ')}` : ''}
        </span>
      </div>
    ))}
  </div>
);

const Suppliers = ({ suppliers = [] }) => (
  <div className="forge-art">
    <div className="forge-art-hd"><Factory size={13} /> Suppliers · {suppliers.length}</div>
    {suppliers.length === 0 && <div className="forge-art-empty">None yet.</div>}
    {suppliers.map((s, i) => {
      const [label, color] = SUPPLIER_STATUS[s.status] || [s.status || '—', '#6b7785'];
      const neg = s.negotiation_state ? (NEG_STATE[s.negotiation_state] || [s.negotiation_state, '#6b7785']) : null;
      return (
        <div key={i} className="forge-art-row forge-art-row-sup">
          <span className="forge-art-main">{s.company}</span>
          <span className="forge-art-pill" style={{ background: `${color}1a`, color }}>{label}</span>
          {neg && <span className="forge-art-pill" style={{ background: `${neg[1]}1a`, color: neg[1] }}>{neg[0]}</span>}
          {s.has_quote && <span className="forge-art-pill" style={{ background: '#05966915', color: '#059669' }}>quote</span>}
          <span className="forge-art-sub">
            {s.line_items?.length ? `lines ${s.line_items.join(', ')}` : ''}
            {s.reliability != null ? ` · rel ${Math.round(s.reliability)}` : ''}
            {s.country ? ` · ${s.country}` : ''}
            {s.last_msg_at ? ` · ${ago(s.last_msg_at)}` : ''}
          </span>
          <ConvLink url={s.conversation_url} />
        </div>
      );
    })}
  </div>
);

const SupplierConversation = ({ company, state, summary, has_quote, conversation_url, messages = [] }) => {
  const neg = state ? (NEG_STATE[state] || [state, '#6b7785']) : null;
  return (
    <div className="forge-art">
      <div className="forge-art-hd"><MessageSquare size={13} /> {company}
        {neg && <span className="forge-art-pill" style={{ background: `${neg[1]}1a`, color: neg[1], marginLeft: 6 }}>{neg[0]}</span>}
        {has_quote && <span className="forge-art-pill" style={{ background: '#05966915', color: '#059669', marginLeft: 6 }}>quote</span>}
      </div>
      {summary && <div className="forge-art-note">{summary}</div>}
      {messages.length === 0 && <div className="forge-art-empty">No transcript logged yet.</div>}
      {messages.map((m, i) => (
        <div key={i} className={`forge-art-conv-msg ${m.from === 'supplier' ? 'inbound' : 'outbound'}`}>
          <span className="forge-art-conv-who">{m.from === 'supplier' ? company : 'Accio'}</span>
          <span className="forge-art-conv-body">{m.body}</span>
        </div>
      ))}
      <ConvLink url={conversation_url} />
    </div>
  );
};

const Quotes = ({ line_items = [], onAction, busy }) => (
  <div className="forge-art">
    <div className="forge-art-hd"><Tag size={13} /> Quote comparison</div>
    {line_items.map((li) => (
      <div key={li.line_item} className="forge-art-block">
        <div className="forge-art-blockhd">#{li.line_item} {li.product_name}</div>
        {(li.quotes || []).length === 0 && <div className="forge-art-empty">No quotes yet.</div>}
        {(li.quotes || []).map((q, i) => (
          <div key={i} className={`forge-art-quote ${q.awarded ? 'awarded' : ''}`}>
            <span className="forge-art-main">{q.factory || '—'}</span>
            <span className="forge-art-price">{money(q.unit_price, q.currency)}</span>
            <span className="forge-art-sub">
              {q.moq != null ? `MOQ ${q.moq}` : ''}
              {q.lead_time_weeks != null ? ` · ${q.lead_time_weeks}w` : ''}
              {q.reliability != null ? ` · rel ${Math.round(q.reliability)}` : ''}
            </span>
            {q.awarded ? (
              <span className="forge-art-awardwrap">
                <span className="forge-art-award">awarded</span>
                {onAction && (
                  <button className="forge-art-btn" disabled={busy}
                    onClick={() => onAction('clear_award', { line_item: li.line_item })}>
                    <X size={12} /> Un-award
                  </button>
                )}
              </span>
            ) : (onAction && q.quote_id && (
              <button className="forge-art-btn" disabled={busy}
                onClick={() => onAction('award_line_item', { line_item: li.line_item, quote_id: q.quote_id })}>
                <Gavel size={12} /> Award
              </button>
            ))}
          </div>
        ))}
      </div>
    ))}
  </div>
);

const Confirm = ({ data = {}, onAction, busy }) => {
  const [done, setDone] = useState(null); // null | 'confirmed' | 'cancelled'
  if (done === 'cancelled') return <div className="forge-art forge-art-confirm cancelled">Cancelled.</div>;
  if (done === 'confirmed') return null;   // the appended system note carries the result
  return (
    <div className="forge-art forge-art-confirm">
      <div className="forge-art-confirm-body">
        <AlertTriangle size={14} />
        <div>
          <div className="forge-art-confirm-title">{data.title || 'Confirm action'}</div>
          {data.detail && <div className="forge-art-confirm-detail">{data.detail}</div>}
        </div>
      </div>
      <div className="forge-art-confirm-actions">
        <button className="forge-btn-ghost" disabled={busy} onClick={() => setDone('cancelled')}>
          <X size={13} /> Cancel
        </button>
        <button className="forge-art-btn primary" disabled={busy}
          onClick={async () => { await onAction(data.action, data.params); setDone('confirmed'); }}>
          <Check size={13} /> {data.confirm_label || 'Confirm'}
        </button>
      </div>
    </div>
  );
};

const Validation = ({ overall_pass, summary, lines = [] }) => (
  <div className="forge-art">
    <div className={`forge-art-gate ${overall_pass ? 'pass' : 'fail'}`}>
      {overall_pass ? <ShieldCheck size={14} /> : <AlertTriangle size={14} />}
      {overall_pass ? 'Ready to submit' : 'Needs attention'}
      {summary ? <span className="forge-art-gate-sub">{summary}</span> : null}
    </div>
    {lines.map((l) => (
      <div key={l.line_item} className="forge-art-row">
        <span className="forge-art-num">#{l.line_item}</span>
        <span className="forge-art-main">{l.product}</span>
        <span className="forge-art-pill" style={l.status === 'validated'
          ? { background: '#05966915', color: '#059669' } : { background: '#d9770615', color: '#d97706' }}>
          {l.status === 'validated' ? 'validated' : 'attention'}
        </span>
        {l.issues?.length ? <span className="forge-art-sub">{l.issues[0]}</span> : null}
      </div>
    ))}
  </div>
);

const MODE_LABEL = { source: 'Source suppliers', negotiate: 'Negotiate quotes', search_only: 'Search listings' };

const Sourcing = ({ mode, relayed }) => (
  <div className="forge-art forge-art-sourcing">
    <Zap size={14} />
    <div>
      <div className="forge-art-confirm-title">Dispatched to Accio · {MODE_LABEL[mode] || mode}</div>
      <div className="forge-art-confirm-detail">
        {relayed
          ? 'Accio is working in the background — suppliers & quotes will appear here.'
          : "Queued, but the Accio bridge didn't confirm (it may be offline)."}
      </div>
    </div>
  </div>
);

const AccioMessage = ({ message, relayed }) => (
  <div className="forge-art forge-art-sourcing">
    <MessageSquare size={14} />
    <div>
      <div className="forge-art-confirm-title">Message to Accio</div>
      <div className="forge-art-note" style={{ border: 'none', padding: '2px 0' }}>&ldquo;{message}&rdquo;</div>
      <div className="forge-art-confirm-detail">
        {relayed ? 'Delivered to the live agent — replies appear in the Accio pane.'
          : "Stored, but the Accio bridge didn't confirm (it may be offline)."}
      </div>
    </div>
  </div>
);

const REC_COLOR = { BID: '#059669', CONSIDER: '#d97706', PASS: '#b91c1c' };

const Pricing = ({ bid_recommendation, recommended_markup_pct, pricing_comparisons = [],
                   historical_contracts = [], market_pricing = [], pricing_summary }) => (
  <div className="forge-art">
    <div className="forge-art-hd"><TrendingUp size={13} /> Pricing intelligence</div>
    {(bid_recommendation || recommended_markup_pct != null) && (
      <div className="forge-art-row">
        {bid_recommendation && (
          <span className="forge-art-pill" style={{ background: `${REC_COLOR[bid_recommendation] || '#6b7785'}1a`, color: REC_COLOR[bid_recommendation] || '#6b7785' }}>
            {bid_recommendation}
          </span>
        )}
        {recommended_markup_pct != null && <span className="forge-art-sub">rec. markup {recommended_markup_pct}%</span>}
      </div>
    )}
    {pricing_comparisons.length > 0 && <div className="forge-art-blockhd">Per-product anchors</div>}
    {pricing_comparisons.map((p, i) => (
      <div key={i} className="forge-art-row">
        <span className="forge-art-main">{p.product_name}</span>
        <span className="forge-art-sub">
          {p.historical_price_paid != null ? `gov paid $${p.historical_price_paid}` : ''}
          {p.our_wholesale_estimate != null ? ` · wholesale ~$${p.our_wholesale_estimate}` : ''}
          {p.oem_estimate != null ? ` · OEM ~$${p.oem_estimate}` : ''}
        </span>
      </div>
    ))}
    {historical_contracts.length > 0 && <div className="forge-art-blockhd">Historical contracts</div>}
    {historical_contracts.map((c, i) => (
      <div key={i} className="forge-art-row">
        <span className="forge-art-main" style={{ fontWeight: 500, fontSize: 12 }}>{c.title}</span>
        <span className="forge-art-sub">
          {c.value != null ? `$${c.value}` : ''}{c.supplier ? ` · ${c.supplier}` : ''}{c.award_date ? ` · ${c.award_date}` : ''}
        </span>
      </div>
    ))}
    {market_pricing.length > 0 && <div className="forge-art-blockhd">Market references</div>}
    {market_pricing.map((m, i) => (
      <div key={i} className="forge-art-row">
        <span className="forge-art-main">{m.product_name}</span>
        <span className="forge-art-price">{m.listed_price != null ? money(m.listed_price, m.currency || 'CAD') : '—'}</span>
        <span className="forge-art-sub">{m.source_name || ''}</span>
      </div>
    ))}
    {pricing_summary && <div className="forge-art-note">{pricing_summary}</div>}
  </div>
);

const ChatArtifact = ({ artifact, onAction, busy }) => {
  if (!artifact) return null;
  const d = artifact.data || {};
  switch (artifact.kind) {
    case 'line_items': return <LineItems items={d.items} />;
    case 'suppliers': return <Suppliers suppliers={d.suppliers} />;
    case 'quotes': return <Quotes line_items={d.line_items} onAction={onAction} busy={busy} />;
    case 'validation': return <Validation {...d} />;
    case 'confirm': return <Confirm data={d} onAction={onAction} busy={busy} />;
    case 'sourcing': return <Sourcing {...d} />;
    case 'accio_message': return <AccioMessage {...d} />;
    case 'supplier_conversation': return <SupplierConversation {...d} />;
    case 'pricing': return <Pricing {...d} />;
    default: return null;
  }
};

export default ChatArtifact;
